import { useEffect, useState } from "react";

import { deepEquals, getElementDefinition, getTypedPropertyValue, TypedValue } from "@medplum/core";
import {
  QuestionnaireItem,
  QuestionnaireItemInitial,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from "@medplum/fhirtypes";
import { ResourcePropertyDisplay } from "@medplum/react";

import { Radio } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { getNestedFormAnswer, setFormFieldValue } from "../../utils/intake-forms";
import {
  questionnaireFieldTestId,
  questionnaireRadioOptionTestId,
} from "../../utils/questionnaire-field-testid";

interface QuestionnaireRadioInputProps<T> {
  readonly name: string;
  readonly label: string;
  readonly item: QuestionnaireItem;
  readonly initial: QuestionnaireItemInitial | undefined;
  readonly response?: QuestionnaireResponseItem;
  form: UseFormReturnType<T>;
  readonly onChangeAnswer: (
    newResponseAnswer: QuestionnaireResponseItemAnswer | QuestionnaireResponseItemAnswer[],
  ) => void;
}

function getItemValue(answer: QuestionnaireResponseItemAnswer): TypedValue {
  return getTypedPropertyValue(
    { type: "QuestionnaireItemAnswer", value: answer },
    "value",
  ) as TypedValue;
}

function getCurrentAnswer(
  response: QuestionnaireResponseItem | undefined,
  index: number = 0,
): TypedValue {
  const results = response?.answer;
  return getItemValue(results?.[index] ?? {});
}

function getCurrentRadioAnswer(
  options: [string, TypedValue][],
  defaultAnswer: TypedValue,
): string | undefined {
  return options.find((option) => deepEquals(option[1].value, defaultAnswer?.value))?.[0];
}

/**
 * Creates a QuestionnaireResponseItemAnswer based on the item type and option value
 * @param item The questionnaire item
 * @param optionValue The selected option value
 * @returns A properly formatted QuestionnaireResponseItemAnswer
 */
function createAnswer(
  item: QuestionnaireItem,
  optionValue: string | Record<string, unknown>,
): QuestionnaireResponseItemAnswer {
  const hasValueSet = item.answerValueSet !== undefined;

  if (hasValueSet) {
    // For questions with answerValueSet, use valueCoding
    if (typeof optionValue === "string") {
      // Convert string to Coding
      return {
        valueCoding: {
          code: optionValue,
          display: optionValue,
        },
      };
    }
    return { valueCoding: optionValue };
  }

  // For questions without answerValueSet, use valueString
  return { valueString: optionValue as string };
}

export default function QuestionnaireRadioInputField<T>(
  props: QuestionnaireRadioInputProps<T>,
): JSX.Element {
  const { name, item, onChangeAnswer, label, response, form } = props;
  const valueElementDefinition = getElementDefinition("QuestionnaireItemAnswerOption", "value[x]");

  const options: [string, TypedValue][] = [];
  const defaultValue = undefined;

  if (item.answerOption) {
    for (let i = 0; i < item.answerOption.length; i++) {
      const option = item.answerOption[i];
      const optionName = `${name}-option-${i}`;
      const optionValue = getTypedPropertyValue(
        { type: "QuestionnaireItemAnswerOption", value: option },
        "value",
      ) as TypedValue;

      if (!optionValue?.value) {
        continue;
      }

      options.push([optionName, optionValue]);
    }
  }

  // Get the current form value - support nested paths for formsets
  const formValue = getNestedFormAnswer(form.values as Record<string, unknown>, name) as
    | T[keyof T]
    | undefined;

  // Find the option that matches the form value
  const formValueOption = formValue
    ? options.find((option) => {
        const optionValue = option[1].value;
        if (typeof formValue === "object" && formValue !== null) {
          // Handle both direct coding objects and valueCoding objects
          const coding = "valueCoding" in formValue ? formValue.valueCoding : formValue;
          return deepEquals(optionValue, coding);
        }
        // Handle string values (for valueString answers like "Yes" or "No")
        if (typeof formValue === "string" && typeof optionValue === "string") {
          return formValue === optionValue;
        }
        // Handle primitive value comparisons
        return formValue === optionValue;
      })
    : undefined;

  const defaultAnswer = getCurrentAnswer(response);
  const answerLinkId = getCurrentRadioAnswer(options, defaultAnswer);

  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    formValueOption?.[0] ?? answerLinkId ?? defaultValue,
  );

  useEffect(() => {
    // Only update the selected value if the form value changes
    // This prevents the radio from getting stuck when there are initial values
    if (formValue !== undefined && formValue !== null) {
      const newSelectedValue = formValueOption?.[0];
      if (newSelectedValue && newSelectedValue !== selectedValue) {
        setSelectedValue(newSelectedValue);
      }
    }
  }, [formValue, formValueOption, selectedValue]);

  const fieldTestId = questionnaireFieldTestId(name);

  return (
    <Radio.Group
      name={name}
      label={label}
      value={selectedValue}
      error={form.errors[name]}
      data-testid={fieldTestId}
      onChange={(newValue) => {
        const option = options.find((option) => option[0] === newValue);
        if (option) {
          setSelectedValue(newValue);
          const optionValue = option[1].value;

          // Use the extracted helper function to create the answer
          const answer = createAnswer(item, optionValue);

          onChangeAnswer(answer);
          setFormFieldValue(form, name, optionValue);
        }
      }}
    >
      {options.map(([optionName, optionValue]) => {
        const optionLabel =
          typeof optionValue.value === "string"
            ? optionValue.value
            : ((optionValue.value as { display?: string })?.display ?? "");
        const optionTestId = questionnaireRadioOptionTestId(name, optionLabel);

        return (
          <Radio
            py={4}
            id={optionName}
            key={optionName}
            value={optionName}
            data-testid={optionTestId}
            label={
              <ResourcePropertyDisplay
                value={optionValue.value}
                propertyType={optionValue.type}
                property={valueElementDefinition}
              />
            }
          />
        );
      })}
    </Radio.Group>
  );
}
